'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CallHookReturn } from '@trueserve/phone-emulator';

export interface AmazonConnectWebRTCCallConfig {
  integrationId: string;
  contactFlowId: string;
  contactFlowName?: string | null;
  participantDisplayName: string;
  attributes: Record<string, string>;
  preview?: { smsDeliveryMode: 'demo_notification' | 'real_sms' };
}

interface StartContactResponse {
  region: string;
  contactId: string | null;
  participantToken: string | null;
  previewSessionId?: string;
  notificationSessionId?: string;
  connectionData?: {
    Meeting?: unknown;
    Attendee?: unknown;
  };
}

type ChimeMeetingSession = {
  audioVideo: {
    addObserver: (observer: Record<string, unknown>) => void;
    bindAudioElement: (audioElement: HTMLAudioElement) => void;
    listAudioInputDevices: () => Promise<Array<{ deviceId?: string }>>;
    startAudioInput: (device: string | MediaStream) => Promise<void>;
    start: () => Promise<void> | void;
    stop: () => void;
    stopAudioInput?: () => Promise<void> | void;
    realtimeMuteLocalAudio: () => void;
    realtimeUnmuteLocalAudio: () => void;
  };
  destroy?: () => Promise<void>;
};

type AmazonConnectCallHookReturn = Omit<CallHookReturn, 'startCall'> & {
  previewSessionId: string | null;
  notificationSessionId: string | null;
  startCall: (config: AmazonConnectWebRTCCallConfig) => Promise<void>;
  getLocalAudioTrack: () => MediaStreamTrack | null;
  getRemoteAudioTrack: () => MediaStreamTrack | null;
  replaceLocalAudio: (track: MediaStreamTrack | null) => Promise<boolean>;
};

function createClientToken(): string {
  return `emulator-${crypto.randomUUID()}`;
}

export function useAmazonConnectWebRTCCall(): AmazonConnectCallHookReturn {
  const [status, setStatus] = useState<CallHookReturn['status']>('idle');
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [previewSessionId, setPreviewSessionId] = useState<string | null>(null);
  const [notificationSessionId, setNotificationSessionId] = useState<string | null>(null);

  const meetingSessionRef = useRef<ChimeMeetingSession | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const participantTokenRef = useRef<string | null>(null);
  const regionRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const microphoneRef = useRef<MediaStream | null>(null);
  const outgoingTrackRef = useRef<MediaStreamTrack | null>(null);
  const generationRef = useRef(0);
  const requestRef = useRef<AbortController | null>(null);

  const cleanupMedia = useCallback(() => {
    generationRef.current += 1;
    requestRef.current?.abort();
    requestRef.current = null;
    const meetingSession = meetingSessionRef.current;
    meetingSessionRef.current = null;
    if (meetingSession) {
      try { meetingSession.audioVideo.stopAudioInput?.(); } catch { /* best effort */ }
      try { meetingSession.audioVideo.stop(); } catch { /* best effort */ }
      meetingSession.destroy?.().catch(() => {});
    }

    const audioElement = audioElementRef.current;
    audioElementRef.current = null;
    if (audioElement) {
      audioElement.pause();
      audioElement.srcObject = null;
    }

    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    microphoneRef.current?.getTracks().forEach(track => track.stop());
    microphoneRef.current = null;
    outgoingTrackRef.current = null;
    setAnalyserNode(null);
    participantTokenRef.current = null;
    regionRef.current = null;
  }, []);

  useEffect(() => {
    if (status === 'connected') {
      setDuration(0);
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  const bindRemoteAudio = useCallback((meetingSession: ChimeMeetingSession) => {
    const audioElement = new Audio();
    audioElement.autoplay = true;
    meetingSession.audioVideo.bindAudioElement(audioElement);
    audioElementRef.current = audioElement;

    try {
      const AudioContextCtor = window.AudioContext ||
        (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return;
      const audioContext = new AudioContextCtor();
      const source = audioContext.createMediaElementSource(audioElement);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyser.connect(audioContext.destination);
      audioContextRef.current = audioContext;
      setAnalyserNode(analyser);
    } catch {
      setAnalyserNode(null);
    }
  }, []);

  const startCall = useCallback(async (config: AmazonConnectWebRTCCallConfig) => {
    cleanupMedia();
    const generation = generationRef.current;
    const controller = new AbortController();
    requestRef.current = controller;
    setStatus('calling');
    setError(null);
    setDuration(0);
    setConversationId(null);
    setPreviewSessionId(null);
    setNotificationSessionId(null);
    setIsMuted(false);

    try {
      const startResponse = await fetch('/api/emulator/amazon-connect/webrtc-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          ...config,
          clientToken: createClientToken(),
        }),
      });
      if (!startResponse.ok) {
        const body = await startResponse.json().catch(() => ({}));
        throw new Error(body.error || `Amazon Connect call failed: ${startResponse.status}`);
      }

      const startData = (await startResponse.json()) as StartContactResponse;
      if (generation !== generationRef.current) return;
      setPreviewSessionId(startData.previewSessionId ?? null);
      setNotificationSessionId(startData.notificationSessionId ?? null);
      if (!startData.connectionData?.Meeting || !startData.connectionData?.Attendee) {
        throw new Error('Amazon Connect did not return Chime meeting data');
      }
      if (!startData.participantToken) {
        throw new Error('Amazon Connect did not return a participant token');
      }

      const chime = await import('amazon-chime-sdk-js');
      if (generation !== generationRef.current) return;
      const logger = new chime.ConsoleLogger('AmazonConnectWebRTC', chime.LogLevel.INFO);
      const deviceController = new chime.DefaultDeviceController(logger);
      const configuration = new chime.MeetingSessionConfiguration(
        { Meeting: startData.connectionData.Meeting },
        { Attendee: startData.connectionData.Attendee },
      );
      const meetingSession = new chime.DefaultMeetingSession(
        configuration,
        logger,
        deviceController,
      ) as ChimeMeetingSession;

      meetingSessionRef.current = meetingSession;
      participantTokenRef.current = startData.participantToken;
      regionRef.current = startData.region;
      setConversationId(startData.contactId);
      bindRemoteAudio(meetingSession);

      meetingSession.audioVideo.addObserver({
        audioVideoDidStart: () => { if (generation === generationRef.current) setStatus('connected'); },
        audioVideoDidStop: () => {
          if (generation !== generationRef.current) return;
          cleanupMedia();
          setStatus((current) => (current === 'error' ? current : 'ended'));
        },
      });

      // Own the mic stream so Preview can mute, mix both sides into recordings,
      // and release a late permission grant after the caller has hung up.
      const microphone = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      if (generation !== generationRef.current) { microphone.getTracks().forEach(track => track.stop()); return; }
      microphoneRef.current = microphone;
      outgoingTrackRef.current = microphone.getAudioTracks()[0] || null;
      await meetingSession.audioVideo.startAudioInput(microphone);
      if (generation !== generationRef.current) return;
      await meetingSession.audioVideo.start();
      if (generation !== generationRef.current) return;
      setStatus((current) => (current === 'calling' ? 'connected' : current));
    } catch (err: unknown) {
      if (generation !== generationRef.current) return;
      cleanupMedia();
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  }, [bindRemoteAudio, cleanupMedia]);

  const endCall = useCallback(() => {
    cleanupMedia();
    setStatus('ended');
  }, [cleanupMedia]);

  const toggleMute = useCallback(() => {
    const meetingSession = meetingSessionRef.current;
    if (!meetingSession) return;
    const nextMuted = !isMuted;
    if (nextMuted) {
      meetingSession.audioVideo.realtimeMuteLocalAudio();
    } else {
      meetingSession.audioVideo.realtimeUnmuteLocalAudio();
    }
    setIsMuted(nextMuted);
  }, [isMuted]);

  const sendDTMF = useCallback((digit: string) => {
    const generation = generationRef.current;
    const participantToken = participantTokenRef.current;
    const region = regionRef.current;
    if (!participantToken || !region) return;
    fetch('/api/emulator/amazon-connect/dtmf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ region, participantToken, digit }),
    }).then(async response => {
      if (!response.ok) throw new Error('Could not send the keypad digit to Amazon Connect.');
    }).catch(() => {
      if (generation === generationRef.current) setError('Could not send the keypad digit to Amazon Connect.');
    });
  }, []);

  const getLocalAudioTrack = useCallback(() => outgoingTrackRef.current, []);
  const getRemoteAudioTrack = useCallback(() => {
    const stream = audioElementRef.current?.srcObject;
    return stream instanceof MediaStream ? stream.getAudioTracks()[0] || null : null;
  }, []);
  const replaceLocalAudio = useCallback(async (track: MediaStreamTrack | null) => {
    const generation = generationRef.current;
    const meeting = meetingSessionRef.current;
    const replacement = track || microphoneRef.current?.getAudioTracks()[0];
    if (!meeting || !replacement) return false;
    try {
      await meeting.audioVideo.startAudioInput(new MediaStream([replacement]));
      if (generation !== generationRef.current) return false;
      outgoingTrackRef.current = replacement;
      return true;
    } catch { return false; }
  }, []);

  useEffect(() => {
    return () => {
      cleanupMedia();
    };
  }, [cleanupMedia]);

  return {
    status,
    duration,
    error,
    analyserNode,
    conversationId,
    previewSessionId,
    notificationSessionId,
    startCall,
    endCall,
    toggleMute,
    isMuted,
    sendDTMF,
    getLocalAudioTrack,
    getRemoteAudioTrack,
    replaceLocalAudio,
  };
}
